import { fabric } from 'fabric';
import {
  getGradientAngleFromFabricGradientCoords,
  getFabricGradient,
} from '../gradients';

const stateProperties = fabric.Rect.prototype.stateProperties.concat();
stateProperties.push('innerStroke', 'innerStrokeWidth');

const cacheProperties = fabric.Rect.prototype.cacheProperties.concat();
cacheProperties.push('innerStroke', 'innerStrokeWidth');

const LudusRect = fabric.util.createClass(fabric.Rect, {
  type: 'ludusRect',
  points: [],
  stateProperties,
  cacheProperties,
  fill: null,

  initialize(options) {
    const optionsFixed = {
      ...options,
      ludus: { ...options.ludus, type: 'rectangle' },
    };
    if (options.innerStroke) {
      optionsFixed.stroke = options.innerStroke;
      delete optionsFixed.innerStroke;
    }
    if (options.innerStrokeWidth) {
      optionsFixed.strokeWidth = options.innerStrokeWidth || 0;
      delete optionsFixed.innerStrokeWidth;
    }
    this.callSuper('initialize', optionsFixed);
    this.strokeMiterLimit = 0;
    this.on('scaling', this.onScaling.bind(this));
    this.on('modified', this.onModified.bind(this));

    this.initialized = true;
  },

  getMaxBorderRadius() {
    const maxRx = (this.width * this.scaleX) / 2;
    const maxRy = (this.height * this.scaleY) / 2;
    const maxR = Math.min(maxRx, maxRy);
    return Math.max(maxR, 0);
  },

  applyGroupScale() {
    if (this.group) {
      const scale =
        this.group.scaleX > this.group.scaleY
          ? this.group.scaleY
          : this.group.scaleX;

      this.set({
        strokeWidth: this.strokeWidth * scale,
        rx: this.rx ? this.rx * scale : this.rx,
        ry: this.ry ? this.ry * scale : this.ry,
      });
    }
  },

  getRenderedRadius(strokeWidth, options) {
    let renderedRadius = this.rx;
    const scaledH =
      (this.height - strokeWidth / options.scale.y) * options.scale.y;
    const scaledW =
      (this.width - strokeWidth / options.scale.x) * options.scale.x;
    if (scaledW <= scaledH) {
      if (this.rx >= scaledW / 2) {
        renderedRadius = scaledW / 2;
      }
    } else if (scaledW > scaledH) {
      if (this.rx >= scaledH / 2) {
        renderedRadius = scaledH / 2;
      }
    }

    return Math.round(renderedRadius);
  },

  onModified() {},

  onScaling() {
    if ((this.width * this.scaleX) / 2 < this.strokeWidth) {
      this.strokeWidth = (this.width * this.scaleX) / 2;
    }
    if ((this.height * this.scaleY) / 2 < this.strokeWidth) {
      this.strokeWidth = (this.height * this.scaleY) / 2;
    }
    this.setCoords();
  },

  _set(key, value) {
    if (!this.initialized) {
      this.callSuper('_set', key, value);
      return this;
    }
    let newValue = value;
    if (key === 'rx' || key === 'ry') {
      newValue = value;
    }

    if (key === 'stroke') {
      if (!value) {
        newValue = 'transparent';
      }
    }
    if (key === 'innerStroke') {
      if (!value) {
        this.strokeWidth = 0;
      } else if (this.strokeWidth === 0) {
        this.strokeWidth = 5;
      }
      this.stroke = newValue;
    }
    if (key === 'innerStrokeWidth') {
      this.strokeWidth = newValue;
    }

    this.callSuper('_set', key, newValue);

    return this;
  },

  setCoords(ignoreZoom, skipAbsolute) {
    const coords = this.callSuper('setCoords', ignoreZoom, skipAbsolute);
    this.updateGradient();
    return coords;
  },

  updateGradient(offsetX = 0, offsetY = 0) {
    if (!this.fill || !this.fill.coords) {
      return;
    }

    const angle = getGradientAngleFromFabricGradientCoords(this.fill.coords);
    const gradient = getFabricGradient(
      {
        ...this.fill,
        angle,
      },
      this.width,
      this.height,
      offsetX,
      offsetY,
    );
    this.fill = gradient;
  },

  scaleVector(p1, p2, factor) {
    const { x: x1, y: y1 } = p1;
    const { x: x2, y: y2 } = p2;
    const x = x2 - x1;
    const y = y2 - y1;
    const dx = x - x * factor;
    const dy = y - y * factor;
    return {
      x: x2 - dx,
      y: y2 - dy,
    };
  },

  makeBezierPoints(p1, p2, p3, radius) {
    // Angle between lines
    const { PI, sqrt, pow, acos, tan, min, max } = Math;
    const { x: x1, y: y1 } = p1;
    const { x: x2, y: y2 } = p2;
    const { x: x3, y: y3 } = p3;
    const a = sqrt(pow(x2 - x1, 2) + pow(y2 - y1, 2));
    const b = sqrt(pow(x3 - x2, 2) + pow(y3 - y2, 2));
    const c = sqrt(pow(x3 - x1, 2) + pow(y3 - y1, 2));
    const cosC = (pow(a, 2) + pow(b, 2) - pow(c, 2)) / (2 * a * b); // cos theorem’s
    const angle = acos(min(max(-1, cosC), 1)); // clamp acos parameter to [-1, 1]
    // Angle between any side and line from circle center to angle vertex
    const angle2 = PI / 2 - angle / 2;
    // Distance between angle vertex and point where circle touches any side
    const side = radius * tan(angle2);
    // How much new sides becomes shorter
    const aCoef = (a - side) / a;
    const bCoef = (b - side) / b;
    return [this.scaleVector(p1, p2, aCoef), this.scaleVector(p3, p2, bCoef)];
  },

  _render(ctx) {
    ctx.save();
    const skipGroup = this.group && this.group.type === 'ludusPathGroup';
    const transformMatrix = this.calcTransformMatrix(skipGroup);
    const options = fabric.util.qrDecompose(transformMatrix);
    const invertMatrix = fabric.util.invertTransform(transformMatrix);
    const strokeWidth =
      this.stroke && this.stroke !== 'transparent' && this.strokeWidth
        ? this.strokeWidth
        : 0;

    const w = this.width - strokeWidth / options.scaleX;
    const h = this.height - strokeWidth / options.scaleY;

    let rx = this.rx / options.scaleX;
    let ry = this.ry / options.scaleY;
    const hw = w / 2;
    const hh = h / 2;
    const sw = w * options.scaleX;
    const sh = h * options.scaleY;

    if (rx > w / 2) {
      rx = sw / 2 / options.scaleX;
      ry = sw / 2 / options.scaleY;
    }
    if (ry > h / 2) {
      ry = sh / 2 / options.scaleY;
      rx = sh / 2 / options.scaleX;
    }

    ctx.beginPath();
    ctx.roundedRectangle(-hw, -hh, w, h, rx, ry);
    ctx.closePath();

    if (this.fill) {
      ctx.fill();
    }

    if (this.stroke && this.strokeWidth > 0) {
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'miter';
      ctx.miterLimit = 100;
      ctx.transform(...invertMatrix);
      if (this.strokeDashArray) {
        ctx.setLineDash(this.strokeDashArray);
      } else {
        ctx.setLineDash([0]);
      }
      ctx.shadowColor = 'transparent';
      ctx.stroke();
      ctx.restore();
    }

    ctx.restore();
  },

  _toSVG() {
    const strokeWidth =
      this.stroke && this.stroke !== 'transparent' && this.strokeWidth
        ? this.strokeWidth
        : 0;
    this.strokeWidth = strokeWidth;
    this.strokeLineCap = 'round';

    const K = (4 * (Math.SQRT2 - 1)) / 3; // constant for circles using Bezier curve.
    const w = this.width - strokeWidth / this.scaleX;
    const h = this.height - strokeWidth / this.scaleY;
    let rx = this.rx / this.scaleX;
    let ry = this.ry / this.scaleY;
    const wh = w / 2;
    const hh = h / 2;
    const left = -wh;
    const top = -hh;
    const bottom = hh;
    const right = wh;

    // make sure corner is not bigger than the shape itself (same logic in render())
    if (rx > wh) {
      rx = wh;
      ry = wh * (this.scaleX / this.scaleY);
    }
    if (ry > hh) {
      ry = hh;
      rx = hh * (this.scaleY / this.scaleX);
    }
    // same as ctx.roundedRectangle() custom fn used in render
    return [
      '<path ',
      'COMMON_PARTS',
      `d="M${left + rx} ${top}
      L${right - rx} ${top}
      C${right + rx * (K - 1)} ${top}, 
      ${right} ${top + ry * (1 - K)}, 
      ${right} ${top + ry} 

      L${right} ${bottom - ry}
      C${right} ${bottom + ry * (K - 1)}, 
      ${right + rx * (K - 1)} ${bottom}, 
      ${right - rx} ${bottom} 
    
      L${left + rx} ${bottom}
      C${left + rx * (1 - K)} ${bottom}, 
      ${left} ${bottom + ry * (K - 1)}, 
      ${left} ${bottom - ry} 
          
      L${left} ${top + ry}
      C${left} ${top + ry * (1 - K)}, 
      ${left + rx * (1 - K)} ${top}, 
      ${left + rx} ${top}

      Z`,
      '"/>\n',
    ];
  },
});

LudusRect.fromObject = function fromObject(object, callback) {
  const options = fabric.util.object.clone(object, true);
  if (callback) callback(new LudusRect(options));
};

LudusRect.fromElement = function(element, callback, options) {
  const extend = fabric.util.object.extend;
  if (!element) {
    return callback(null);
  }
  options = options || {};

  var parsedAttributes = fabric.parseAttributes(
    element,
    fabric.Rect.ATTRIBUTE_NAMES,
  );
  parsedAttributes.left = parsedAttributes.left || 0;
  parsedAttributes.top = parsedAttributes.top || 0;
  parsedAttributes.height = parsedAttributes.height || 0;
  parsedAttributes.width = parsedAttributes.width || 0;
  var rect = new fabric.LudusRect(
    extend(options ? fabric.util.object.clone(options) : {}, parsedAttributes),
  );
  rect.visible = rect.visible && rect.width > 0 && rect.height > 0;
  callback(rect);
};

export default LudusRect;
